import { UserAgent } from '../UserAgent';

export type WheelSource = 'mouse' | 'trackpad';
export interface WheelHints { pinch?: boolean, guess?: boolean, shouldScale?: boolean }
export interface WheelInfo { source: WheelSource | null, hints: WheelHints, reason: WheelReason | null }
export type WheelReason =
    'wheelDelta120x' | 'wheelDelta3x' | 'wheelDeltaWinPT' |
    'xDelta' | 'pinchEmulation' | 'rolloverAssumption' | 'firefox' | 'chromeDeltaY';

type SequenceTuple = [number, number, number];

// To get around stupid TypeScript issues swap these commented out lines when developing...
// type WheelEventInfo = WheelEvent;
type WheelEventInfo = any;

const result = (source: WheelSource | null, reason: WheelReason | null, hints: WheelHints = {}): WheelInfo => {
    // console.log(source, reason);
    return ({ source, reason, hints });
};

const all = <T>(a: T[], p: (x: T) => boolean) => {
    for (const x of a) if (!p(x)) return false;
    return a.length > 0;
};

const uniform = <T>(a: T[]) => {
    if (a.length) {
        for (const x of a) if (x !== a[0]) return false;
    }
    return true;
};

const mouse = (reason: WheelReason | null, hints: WheelHints = {}) => result('mouse', reason, hints);
const trackpad = (reason: WheelReason | null, hints: WheelHints = {}) => result('trackpad', reason, hints);

const detectSource = (() => {

    let timeLast = performance.now();
    let infoLast = null as WheelInfo | null;
    let rollover = null as WheelInfo & { count: number } | null;
    const sequence = [] as SequenceTuple[];

    return (e: WheelEventInfo) => {

        const timeNow = performance.now();
        if ((timeNow - timeLast) >= 200) {
            rollover = null;
            sequence.length = 0;
        }

        // If we have rollover with count above threshold just assume for the rest of this sequence
        if (rollover && rollover.count >= 8) {
            return result(rollover.source, 'rolloverAssumption', rollover.hints);
        }

        sequence.push([e.deltaX, e.deltaY, e.wheelDelta]);
        timeLast = timeNow;

        let info = tryDetermine(e, sequence);
        if (info) {
            if (!rollover) {
                rollover = Object.assign({ count: 0 }, info as any);
            }
            if (rollover!.source === info.source) {
                rollover!.count++;
            }
            else {
                rollover = null;
            }
            infoLast = info;
        }
        else {
            // Guessing time, this is where the real hacks are...
            // Based on last result:
            if (infoLast) {
                info = result(infoLast.source, null, { guess: true });
            }
            // If it's Chrome and textarea weirdness
            else if (!!e.target && (e.target as HTMLElement).nodeName === 'TEXTAREA' && e.wheelDelta === undefined) {
                info = mouse(null, { guess: true });
            }
            // Otherwise guess trackpad
            else {
                info = trackpad(null, { guess: true });
            }
        }

        // In firefox, if we don't set this is slows movement down
        info.hints.shouldScale = e.deltaMode > 0;
        return info;
    };
})();

const tryDetermine = (e: WheelEventInfo, sequence: SequenceTuple[]): WheelInfo | null => {
    // Scrolling x; always trackpad...
    if (sequence.find(sx => sx[0] !== 0)) {
        return trackpad('xDelta');
    }
    // Special firefox behavior...
    if (UserAgent.browser.name === 'Firefox') {
        // On firefox, if deltaY is multiple of 3 and whole, it's mouse
        if (!e.ctrlKey && !!all(sequence, sx => sx[1] % 3 === 0)) {
            return mouse('firefox');
        }
        else {
            return trackpad('firefox', { pinch: e.ctrlKey });
        }
    }
    // Special Chrome/Chromium
    if (UserAgent.browser?.name?.startsWith('Chrom')) {
        // On Chrome, abs(deltaY) is uniform and whole then it's mouse
        if (!e.ctrlKey
            && sequence.length >= 3
            && uniform(sequence.map(sx => Math.abs(sx[1])))
            && all(sequence, sx => sx[1] % 1 === 0 && sx[2] === undefined)) {
            return mouse('chromeDeltaY');
        }
    }
    // If ctrl key pressed but was not pressed; probably trackpad pinch...
    if (e.ctrlKey && (e.deltaY * 100) % 2 !== 0) {
        return trackpad('pinchEmulation', { pinch: true });
    }
    // If wheelDelta multiple of 120; always mouse if other tests have failed...
    if (all(sequence, sx => Math.abs(sx[2]) >= 120 && sx[2] % 120 === 0)) {
        return mouse('wheelDelta120x');
    }
    // If wheelDelta multiple of 3; probably trackpad if other tests have failed...
    if (all(sequence, sx => sx[2] % 3 === 0)) {
        return trackpad('wheelDelta3x');
    }
    // Windows Precision Trackpad uses a delta that matches the Y most of the time
    if (e.deltaY === -e.wheelDelta || -e.deltaY === e.wheelDelta) {
        return trackpad('wheelDeltaWinPT');
    }
    // Then we can't tell for sure!
    return null;
};

export class WheelUtil {
    public static inspect = detectSource;
}
